import jsPDF from 'jspdf';
import type {
  Invoice,
  InvoiceItem
} from '../types/unified-financial';
import clinicInfo from '../config/clinicInfo';
import { ClientReportSettingsService } from '../features/assessments-v2/services/ClientReportSettingsService';
import type { ResolvedClientReportBranding } from '../features/assessments-v2/types';
import { InvoiceService } from './InvoiceService';
import type { Client } from './UserService';
import { formatDateForDisplayFromString } from '../utils/dateHelpers';

type PDFColor = readonly [number, number, number];

interface InvoiceStatusPalette {
  fill: PDFColor;
  text: PDFColor;
}

export interface InvoicePDFData {
  invoice: Invoice;
  patientName: string;
  patientId: string;
  clientId?: string;
  selectedClient?: Client | null;
  patientDocument?: string;
  patientAddress?: string;
}

export class InvoicePDFService {
  private static readonly PAGE_MARGIN = 15;
  private static readonly FOOTER_HEIGHT = 22;
  private static readonly BOTTOM_SAFE_MARGIN = 30;
  private static readonly COLORS = {
    slate900: [15, 23, 42] as PDFColor,
    slate800: [30, 41, 59] as PDFColor,
    slate700: [51, 65, 85] as PDFColor,
    slate600: [71, 85, 105] as PDFColor,
    slate500: [100, 116, 139] as PDFColor,
    slate300: [203, 213, 225] as PDFColor,
    slate200: [226, 232, 240] as PDFColor,
    slate100: [241, 245, 249] as PDFColor,
    slate50: [248, 250, 252] as PDFColor,
    brandBlue: [46, 90, 136] as PDFColor,
    emerald: [22, 163, 74] as PDFColor,
    amber: [180, 83, 9] as PDFColor,
    rose: [190, 18, 60] as PDFColor,
    white: [255, 255, 255] as PDFColor
  };

  private static buildFallbackBranding(clientId?: string, selectedClient?: Client | null): ResolvedClientReportBranding {
    const displayName = selectedClient?.clientName || clinicInfo.reportBranding.displayName || clinicInfo.nomeFantasia;
    const logoPrintUrl =
      selectedClient?.logo_wordmark_url ||
      selectedClient?.clientLogoWordmarkUrl ||
      selectedClient?.logo_url ||
      selectedClient?.clientLogoUrl ||
      clinicInfo.reportBranding.logoPrint;
    const logoMarkUrl =
      selectedClient?.logo_url ||
      selectedClient?.clientLogoUrl ||
      clinicInfo.reportBranding.logoMark;

    return {
      clientId: clientId || selectedClient?.clientId || '',
      source: 'fallback',
      displayName,
      legalName: clinicInfo.razaoSocial,
      cnpj: clinicInfo.cnpj,
      reportSubtitle: clinicInfo.especialidades,
      logoPrintUrl,
      logoMarkUrl,
      logoWordmarkUrl: logoPrintUrl,
      logoAlt: displayName,
      addressLine1: clinicInfo.endereco.enderecoCompleto,
      addressLine2: `${clinicInfo.endereco.cidade} - ${clinicInfo.endereco.estado} | CEP: ${clinicInfo.endereco.cep}`,
      contactLine: `${clinicInfo.contato.telefone} | ${clinicInfo.contato.email}`,
      footerLegalLine: `${clinicInfo.razaoSocial} - CNPJ: ${clinicInfo.cnpj}`,
      rawSettings: null
    };
  }

  private static async resolveBranding(data: InvoicePDFData): Promise<ResolvedClientReportBranding> {
    if (!data.clientId) {
      return this.buildFallbackBranding(undefined, data.selectedClient);
    }

    try {
      return await ClientReportSettingsService.resolveForClient(data.clientId, data.selectedClient ?? null);
    } catch (error) {
      console.warn('Falha ao carregar branding do tenant para fatura. Aplicando fallback.', error);
      return this.buildFallbackBranding(data.clientId, data.selectedClient);
    }
  }

  private static async loadImageAsDataUrl(url?: string | null): Promise<string | null> {
    if (!url) return null;

    try {
      const response = await fetch(url);
      const blob = await response.blob();

      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error: any) {
      console.warn('Não foi possível carregar imagem da fatura:', error);
      return null;
    }
  }

  static async generatePDF(data: InvoicePDFData): Promise<jsPDF> {
    if (data.clientId) {
      await InvoiceService.loadBankingInfo(data.clientId);
    }

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const bankingInfo = InvoiceService.getBankingInfo(data.clientId);
    const branding = await this.resolveBranding(data);
    const logoBase64 = await this.loadImageAsDataUrl(
      branding.logoPrintUrl || branding.logoWordmarkUrl || branding.logoMarkUrl
    );
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');

    let yPosition = await this.renderHeader(doc, branding, logoBase64);
    yPosition = this.renderInvoiceIntro(doc, data, yPosition);
    yPosition = this.renderAppointmentSummary(doc, data.invoice, yPosition);
    yPosition = this.renderItemsTable(doc, data.invoice, yPosition);
    yPosition = this.renderPaymentSection(doc, data.invoice, bankingInfo, yPosition);
    yPosition = this.renderNotesSection(doc, data.invoice, yPosition);

    this.renderFooters(doc, branding, revahubMarkBase64);

    return doc;
  }

  static async downloadPDF(data: InvoicePDFData): Promise<void> {
    try {
      const doc = await this.generatePDF(data);
      const fileName = `Fatura_${data.invoice.invoiceId}_${data.patientName.replace(/\s+/g, '_')}.pdf`;
      doc.save(fileName);
    } catch (error: any) {
      console.error('Erro ao gerar PDF:', error);
      throw new Error(`Erro ao gerar PDF: ${error?.message ?? String(error)}`);
    }
  }

  static async printPDF(data: InvoicePDFData): Promise<void> {
    try {
      const doc = await this.generatePDF(data);
      const blob = doc.output('blob');
      const url = URL.createObjectURL(blob);

      const printWindow = window.open(url, '_blank');

      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            printWindow.onafterprint = () => {
              printWindow.close();
              URL.revokeObjectURL(url);
            };
          }, 500);
        };
      } else {
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.top = '-9999px';
        iframe.style.left = '-9999px';
        iframe.style.width = '0px';
        iframe.style.height = '0px';

        document.body.appendChild(iframe);
        iframe.src = url;

        setTimeout(() => {
          iframe.contentWindow?.print();
          setTimeout(() => {
            document.body.removeChild(iframe);
            URL.revokeObjectURL(url);
          }, 1000);
        }, 500);
      }
    } catch (error: any) {
      console.error('Erro ao imprimir PDF:', error);
      throw new Error(`Erro ao imprimir PDF: ${error?.message ?? String(error)}`);
    }
  }

  private static async renderHeader(
    doc: jsPDF,
    branding: ResolvedClientReportBranding,
    logoBase64: string | null
  ): Promise<number> {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = this.PAGE_MARGIN;
    const headerTop = 9;
    const minHeaderHeight = 38;
    const logoWidth = 26;
    const logoHeight = 16;
    const leftX = margin + 34;
    const rightMaxWidth = 62;
    const columnGap = 8;
    const leftMaxWidth = Math.max(58, pageWidth - margin - leftX - rightMaxWidth - columnGap);

    this.applyTextColor(doc, this.COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14.5);
    const titleLines = this.fitTextLines(
      doc,
      branding.displayName || branding.legalName || 'Clínica',
      leftMaxWidth,
      2
    );

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    this.applyTextColor(doc, this.COLORS.slate500);
    const subtitleLines = this.fitTextLines(
      doc,
      branding.reportSubtitle || 'Documento financeiro',
      leftMaxWidth,
      1
    );

    doc.setFontSize(7.8);
    const contactLines = this.getHeaderContactLines(doc, branding, rightMaxWidth, 5);

    const leftTextHeight =
      titleLines.length * 5.8 +
      (subtitleLines.length > 0 ? 1.8 + subtitleLines.length * 4 : 0);
    const rightTextHeight = Math.max(contactLines.length, 1) * 3.8;
    const contentHeight = Math.max(logoHeight, leftTextHeight, rightTextHeight);
    const headerHeight = Math.max(minHeaderHeight, headerTop + contentHeight + 7);
    const logoY = headerTop + Math.max(0, (contentHeight - logoHeight) / 2);
    const titleStartY = headerTop + Math.max(5, (contentHeight - leftTextHeight) / 2) + 4.5;
    const contactStartY = headerTop + Math.max(4, (contentHeight - rightTextHeight) / 2) + 2.8;

    this.applyFillColor(doc, this.COLORS.slate50);
    doc.rect(0, 0, pageWidth, headerHeight, 'F');

    if (logoBase64) {
      try {
        doc.addImage(logoBase64, 'PNG', margin, logoY, logoWidth, logoHeight);
      } catch (error) {
        console.warn('Não foi possível renderizar o logo do tenant na fatura:', error);
      }
    } else {
      const size = 16;
      const logoCenterY = logoY + logoHeight / 2;
      this.applyFillColor(doc, this.COLORS.brandBlue);
      doc.circle(margin + size / 2, logoCenterY, size / 2, 'F');
      this.applyTextColor(doc, this.COLORS.white);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9.5);
      doc.text((branding.displayName || 'C').slice(0, 1).toUpperCase(), margin + size / 2, logoCenterY + 1.5, {
        align: 'center'
      });
    }

    this.applyTextColor(doc, this.COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(titleLines.length > 1 ? 13.2 : 14.5);
    titleLines.forEach((line, index) => {
      doc.text(line, leftX, titleStartY + index * 5.8);
    });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    this.applyTextColor(doc, this.COLORS.slate500);
    subtitleLines.forEach((line, index) => {
      doc.text(line, leftX, titleStartY + titleLines.length * 5.8 + 1.8 + index * 4);
    });

    doc.setFontSize(7.8);
    contactLines.forEach((line, index) => {
      doc.text(line, pageWidth - margin, contactStartY + index * 3.8, { align: 'right' });
    });

    return headerHeight + 8;
  }

  private static renderInvoiceIntro(doc: jsPDF, data: InvoicePDFData, yPosition: number): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = this.PAGE_MARGIN;
    const contentWidth = pageWidth - margin * 2;
    const rightX = pageWidth - margin;
    const statusText = this.getStatusText(data.invoice.status);
    const statusPalette = this.getStatusPalette(data.invoice.status);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    this.applyTextColor(doc, this.COLORS.slate900);
    doc.text('Fatura', margin, yPosition);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.6);
    this.applyTextColor(doc, this.COLORS.slate500);
    doc.text(`Nº ${data.invoice.invoiceId}`, margin, yPosition + 6);

    this.drawStatusBadge(doc, rightX, yPosition - 1.5, statusText, statusPalette);

    yPosition += 15;

    this.applyDrawColor(doc, this.COLORS.slate200);
    doc.line(margin, yPosition, rightX, yPosition);
    yPosition += 8;

    const leftWidth = 96;
    const rightWidth = contentWidth - leftWidth - 10;

    this.renderInfoBlock(doc, margin, yPosition, leftWidth, 'Paciente', [
      ['Nome', data.patientName || 'Não informado'],
      ['CPF', data.patientDocument || 'Não informado'],
      ['Código', data.patientId || 'Não informado']
    ]);

    this.renderInfoBlock(doc, margin + leftWidth + 10, yPosition, rightWidth, 'Cobrança', [
      ['Competência', this.getBillingPeriodLabel(data.invoice)],
      ['Emissão', this.formatDate(data.invoice.createdAt)],
      ['Vencimento', this.formatDate(data.invoice.dueDate)]
    ]);

    yPosition += 39;

    this.applyFillColor(doc, this.COLORS.slate50);
    this.applyDrawColor(doc, this.COLORS.slate200);
    doc.roundedRect(margin, yPosition, contentWidth, 20, 2.5, 2.5, 'FD');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.2);
    this.applyTextColor(doc, this.COLORS.slate500);
    doc.text('Total a pagar', margin + 5, yPosition + 7);
    doc.text('Referente aos lançamentos detalhados nesta fatura.', margin + 5, yPosition + 14);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(17);
    this.applyTextColor(doc, this.COLORS.brandBlue);
    doc.text(this.formatCurrency(data.invoice.total), rightX - 5, yPosition + 12.5, { align: 'right' });

    return yPosition + 29;
  }

  private static renderAppointmentSummary(doc: jsPDF, invoice: Invoice, yPosition: number): number {
    if (!invoice.appointmentDetails) {
      return yPosition;
    }

    yPosition = this.ensureSpace(doc, yPosition, 27, invoice.invoiceId);
    yPosition = this.renderSectionTitle(doc, yPosition, 'Conferência do período');

    const margin = this.PAGE_MARGIN;
    const pageWidth = doc.internal.pageSize.getWidth();
    const labels = [
      ['Realizadas', invoice.appointmentDetails.completed.length],
      ['Faltas', invoice.appointmentDetails.noShow.length],
      ['Canceladas', invoice.appointmentDetails.cancelled.length],
      ['Agendadas', invoice.appointmentDetails.scheduled.length]
    ];
    const totalWidth = pageWidth - margin * 2;
    const itemWidth = totalWidth / labels.length;

    this.applyFillColor(doc, this.COLORS.slate50);
    this.applyDrawColor(doc, this.COLORS.slate200);
    doc.roundedRect(margin, yPosition, totalWidth, 17, 2.5, 2.5, 'FD');

    labels.forEach(([label, value], index) => {
      const x = margin + itemWidth * index;
      if (index > 0) {
        this.applyDrawColor(doc, this.COLORS.slate200);
        doc.line(x, yPosition + 3, x, yPosition + 14);
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10.5);
      this.applyTextColor(doc, this.COLORS.slate800);
      doc.text(String(value), x + 5, yPosition + 8);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.8);
      this.applyTextColor(doc, this.COLORS.slate500);
      doc.text(String(label), x + 5, yPosition + 13);
    });

    return yPosition + 24;
  }

  private static renderItemsTable(doc: jsPDF, invoice: Invoice, yPosition: number): number {
    yPosition = this.ensureSpace(doc, yPosition, 29, invoice.invoiceId);
    yPosition = this.renderSectionTitle(doc, yPosition, 'Detalhamento da cobrança');
    yPosition = this.renderItemsTableHeader(doc, yPosition);

    if (invoice.items.length === 0) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.8);
      this.applyTextColor(doc, this.COLORS.slate500);
      doc.text('Nenhum lançamento cadastrado nesta fatura.', this.PAGE_MARGIN + 2, yPosition + 7);
      return yPosition + 16;
    }

    invoice.items.forEach((item) => {
      const descriptionLines = this.fitTextLines(doc, item.description, 76, 3);
      const rowHeight = Math.max(11, descriptionLines.length * 4 + 5);
      const pageBefore = doc.getNumberOfPages();

      yPosition = this.ensureSpace(doc, yPosition, rowHeight + 4, invoice.invoiceId);
      if (doc.getNumberOfPages() > pageBefore) {
        yPosition = this.renderItemsTableHeader(doc, yPosition);
      }

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.4);
      this.applyTextColor(doc, this.COLORS.slate700);
      doc.text(this.formatDate(item.date), this.PAGE_MARGIN + 2, yPosition + 6.5);
      doc.text(this.getItemTypeLabel(item.type), this.PAGE_MARGIN + 29, yPosition + 6.5);

      this.applyTextColor(doc, this.COLORS.slate800);
      doc.text(descriptionLines, this.PAGE_MARGIN + 56, yPosition + 6.5);

      doc.setFont('helvetica', 'bold');
      this.applyTextColor(doc, this.COLORS.slate900);
      doc.text(this.formatCurrency(item.amount), doc.internal.pageSize.getWidth() - this.PAGE_MARGIN - 2, yPosition + 6.5, {
        align: 'right'
      });

      this.applyDrawColor(doc, this.COLORS.slate200);
      doc.setLineWidth(0.2);
      doc.line(this.PAGE_MARGIN, yPosition + rowHeight, doc.internal.pageSize.getWidth() - this.PAGE_MARGIN, yPosition + rowHeight);

      yPosition += rowHeight;
    });

    yPosition += 4;
    this.applyDrawColor(doc, this.COLORS.slate300);
    doc.line(this.PAGE_MARGIN, yPosition, doc.internal.pageSize.getWidth() - this.PAGE_MARGIN, yPosition);

    yPosition += 7;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.2);
    this.applyTextColor(doc, this.COLORS.slate500);
    doc.text(`${invoice.items.length} lançamento${invoice.items.length === 1 ? '' : 's'}`, this.PAGE_MARGIN, yPosition);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11.5);
    this.applyTextColor(doc, this.COLORS.slate900);
    doc.text(`Total ${this.formatCurrency(invoice.total)}`, doc.internal.pageSize.getWidth() - this.PAGE_MARGIN, yPosition, {
      align: 'right'
    });

    return yPosition + 12;
  }

  private static renderItemsTableHeader(doc: jsPDF, yPosition: number): number {
    const tableWidth = doc.internal.pageSize.getWidth() - this.PAGE_MARGIN * 2;

    this.applyFillColor(doc, this.COLORS.slate100);
    doc.roundedRect(this.PAGE_MARGIN, yPosition, tableWidth, 9.5, 2, 2, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.8);
    this.applyTextColor(doc, this.COLORS.slate600);
    doc.text('Data', this.PAGE_MARGIN + 2, yPosition + 6);
    doc.text('Categoria', this.PAGE_MARGIN + 29, yPosition + 6);
    doc.text('Descrição', this.PAGE_MARGIN + 56, yPosition + 6);
    doc.text('Valor', doc.internal.pageSize.getWidth() - this.PAGE_MARGIN - 2, yPosition + 6, { align: 'right' });

    return yPosition + 11.5;
  }

  private static renderPaymentSection(
    doc: jsPDF,
    invoice: Invoice,
    bankingInfo: {
      bankName: string;
      accountType?: string;
      agency: string;
      account: string;
      pixKey: string;
      pixType: string;
      beneficiary: string;
    },
    yPosition: number
  ): number {
    yPosition = this.ensureSpace(doc, yPosition, 54, invoice.invoiceId);
    yPosition = this.renderSectionTitle(doc, yPosition, 'Dados para pagamento');

    const margin = this.PAGE_MARGIN;
    const pageWidth = doc.internal.pageSize.getWidth();
    const width = pageWidth - margin * 2;
    const pixWidth = 70;
    const boxHeight = 39;

    this.applyFillColor(doc, this.COLORS.white);
    this.applyDrawColor(doc, this.COLORS.slate200);
    doc.roundedRect(margin, yPosition, width, boxHeight, 2.5, 2.5, 'FD');

    this.applyFillColor(doc, this.COLORS.slate50);
    doc.roundedRect(margin + 4, yPosition + 5, pixWidth, 29, 2, 2, 'F');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.8);
    this.applyTextColor(doc, this.COLORS.slate500);
    doc.text(`PIX ${bankingInfo.pixType ? `(${bankingInfo.pixType})` : ''}`.trim(), margin + 8, yPosition + 12);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    this.applyTextColor(doc, this.COLORS.slate900);
    const pixKeyLines = this.fitTextLines(doc, bankingInfo.pixKey || 'Chave não informada', pixWidth - 8, 3);
    doc.text(pixKeyLines, margin + 8, yPosition + 19);

    const detailsX = margin + pixWidth + 11;
    const detailsMaxWidth = width - pixWidth - 17;
    const detailLines = [
      `Beneficiário: ${bankingInfo.beneficiary || 'Não informado'}`,
      `Banco: ${bankingInfo.bankName || 'Não informado'}`,
      `Agência: ${bankingInfo.agency || 'Não informada'}  |  Conta: ${bankingInfo.account || 'Não informada'}`,
      `Identificação: fatura ${invoice.invoiceId} ou nome do paciente`
    ];

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.2);
    this.applyTextColor(doc, this.COLORS.slate700);
    detailLines.forEach((line, index) => {
      doc.text(this.fitTextLines(doc, line, detailsMaxWidth, 1), detailsX, yPosition + 10 + index * 6);
    });

    doc.setFontSize(7.8);
    this.applyTextColor(doc, this.COLORS.slate500);
    const instruction = invoice.dueDate
      ? `Efetue o pagamento até ${this.formatDate(invoice.dueDate)}.`
      : 'Efetue o pagamento conforme orientação da clínica.';
    doc.text(instruction, detailsX, yPosition + 34, { maxWidth: detailsMaxWidth });

    return yPosition + boxHeight + 9;
  }

  private static renderNotesSection(doc: jsPDF, invoice: Invoice, yPosition: number): number {
    if (!invoice.notes) {
      return yPosition;
    }

    const noteLines = doc.splitTextToSize(invoice.notes, doc.internal.pageSize.getWidth() - this.PAGE_MARGIN * 2 - 8);
    const boxHeight = 10 + noteLines.length * 4;

    yPosition = this.ensureSpace(doc, yPosition, boxHeight + 12, invoice.invoiceId);
    yPosition = this.renderSectionTitle(doc, yPosition, 'Observações');

    this.applyFillColor(doc, this.COLORS.slate50);
    this.applyDrawColor(doc, this.COLORS.slate200);
    doc.roundedRect(this.PAGE_MARGIN, yPosition, doc.internal.pageSize.getWidth() - this.PAGE_MARGIN * 2, boxHeight, 2.5, 2.5, 'FD');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.3);
    this.applyTextColor(doc, this.COLORS.slate700);
    doc.text(noteLines, this.PAGE_MARGIN + 4, yPosition + 7);

    return yPosition + boxHeight + 6;
  }

  private static renderSectionTitle(doc: jsPDF, yPosition: number, title: string): number {
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.8);
    this.applyTextColor(doc, this.COLORS.slate800);
    doc.text(title, this.PAGE_MARGIN, yPosition);

    this.applyDrawColor(doc, this.COLORS.slate200);
    doc.setLineWidth(0.35);
    doc.line(this.PAGE_MARGIN, yPosition + 3, pageWidth - this.PAGE_MARGIN, yPosition + 3);

    return yPosition + 8;
  }

  private static renderInfoBlock(
    doc: jsPDF,
    x: number,
    y: number,
    width: number,
    title: string,
    rows: Array<[string, string]>
  ): void {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.2);
    this.applyTextColor(doc, this.COLORS.slate900);
    doc.text(title, x, y);

    rows.forEach(([label, value], index) => {
      const rowY = y + 8 + index * 8;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.6);
      this.applyTextColor(doc, this.COLORS.slate500);
      doc.text(label, x, rowY);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      this.applyTextColor(doc, this.COLORS.slate800);
      doc.text(this.fitTextLines(doc, value, width, 1), x, rowY + 4);
    });
  }

  private static drawStatusBadge(
    doc: jsPDF,
    rightEdge: number,
    y: number,
    text: string,
    palette: InvoiceStatusPalette
  ): void {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.8);

    const badgeWidth = doc.getTextWidth(text) + 9;
    const badgeX = rightEdge - badgeWidth;

    this.applyFillColor(doc, palette.fill);
    doc.roundedRect(badgeX, y, badgeWidth, 7.8, 3.9, 3.9, 'F');

    this.applyTextColor(doc, palette.text);
    doc.text(text, badgeX + badgeWidth / 2, y + 5.1, { align: 'center' });
  }

  private static ensureSpace(doc: jsPDF, yPosition: number, requiredHeight: number, invoiceId: string): number {
    const pageHeight = doc.internal.pageSize.getHeight();

    if (yPosition + requiredHeight <= pageHeight - this.BOTTOM_SAFE_MARGIN) {
      return yPosition;
    }

    doc.addPage();
    return this.renderContinuationHeader(doc, invoiceId);
  }

  private static renderContinuationHeader(doc: jsPDF, invoiceId: string): number {
    const pageWidth = doc.internal.pageSize.getWidth();

    this.applyFillColor(doc, this.COLORS.slate50);
    doc.rect(0, 0, pageWidth, 25, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.2);
    this.applyTextColor(doc, this.COLORS.slate800);
    doc.text('Fatura', this.PAGE_MARGIN, 16);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    this.applyTextColor(doc, this.COLORS.slate500);
    doc.text(`Nº ${invoiceId}`, pageWidth - this.PAGE_MARGIN, 16, { align: 'right' });

    this.applyDrawColor(doc, this.COLORS.slate200);
    doc.line(this.PAGE_MARGIN, 25, pageWidth - this.PAGE_MARGIN, 25);

    return 34;
  }

  private static renderFooters(
    doc: jsPDF,
    branding: ResolvedClientReportBranding,
    revahubMarkBase64: string | null
  ): void {
    const totalPages = doc.getNumberOfPages();

    for (let page = 1; page <= totalPages; page += 1) {
      doc.setPage(page);

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = this.PAGE_MARGIN;

      this.applyFillColor(doc, this.COLORS.slate50);
      doc.rect(0, pageHeight - this.FOOTER_HEIGHT, pageWidth, this.FOOTER_HEIGHT, 'F');
      this.applyDrawColor(doc, this.COLORS.slate200);
      doc.line(margin, pageHeight - this.FOOTER_HEIGHT, pageWidth - margin, pageHeight - this.FOOTER_HEIGHT);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.4);
      this.applyTextColor(doc, this.COLORS.slate500);
      doc.text(`${branding.displayName || branding.legalName} - documento financeiro eletrônico`, margin, pageHeight - 15);
      doc.text(branding.footerLegalLine || 'Documento gerado pelo sistema de gestão clínica.', margin, pageHeight - 10);

      const markSize = 7.6;
      const markX = pageWidth - margin - markSize;
      const markY = pageHeight - 13.2;
      const footerRightX = markX - 3;

      if (revahubMarkBase64) {
        try {
          doc.addImage(revahubMarkBase64, 'PNG', markX, markY, markSize, markSize);
        } catch {
          // Rodape continua legível sem a marca.
        }
      }

      this.applyTextColor(doc, this.COLORS.brandBlue);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.6);
      doc.text('Powered by Revahub', footerRightX, pageHeight - 9.5, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      this.applyTextColor(doc, this.COLORS.slate500);
      doc.text(`Página ${page}/${totalPages}`, footerRightX, pageHeight - 5.8, { align: 'right' });
    }
  }

  private static getHeaderContactLines(
    doc: jsPDF,
    branding: ResolvedClientReportBranding,
    maxWidth: number,
    maxLines = 5
  ): string[] {
    const rawLines = [
      branding.addressLine1,
      branding.addressLine2,
      branding.contactLine,
      branding.cnpj && branding.cnpj !== '—' ? `CNPJ: ${branding.cnpj}` : '',
    ].filter((line) => line && line !== '—') as string[];

    return this.fitTextLines(doc, rawLines.join(' | '), maxWidth, maxLines);
  }

  private static fitTextLines(doc: jsPDF, text: string, maxWidth: number, maxLines: number): string[] {
    const content = String(text || '').trim();
    if (!content) return [];

    const lines = doc.splitTextToSize(content, maxWidth) as string[];
    if (lines.length <= maxLines) return lines;

    const fitted = lines.slice(0, maxLines);
    fitted[maxLines - 1] = this.trimLineToWidth(doc, fitted[maxLines - 1], maxWidth);
    return fitted;
  }

  private static trimLineToWidth(doc: jsPDF, line: string, maxWidth: number): string {
    const ellipsis = '...';
    let candidate = line;

    while (candidate.length > 0 && doc.getTextWidth(`${candidate}${ellipsis}`) > maxWidth) {
      candidate = candidate.slice(0, -1).trimEnd();
    }

    return candidate ? `${candidate}${ellipsis}` : ellipsis;
  }

  private static getBillingPeriodLabel(invoice: Invoice): string {
    if (invoice.billingPeriodStart && invoice.billingPeriodEnd) {
      return `${this.formatDate(invoice.billingPeriodStart)} a ${this.formatDate(invoice.billingPeriodEnd)}`;
    }

    return this.formatMonth(invoice.month);
  }

  private static getItemTypeLabel(type: InvoiceItem['type']): string {
    const labelMap: Record<InvoiceItem['type'], string> = {
      session: 'Sessão',
      package: 'Pacote',
      additional: 'Adicional',
      other: 'Outro'
    };

    return labelMap[type] ?? type;
  }

  private static getStatusText(status: string): string {
    const statusMap: Record<string, string> = {
      open: 'Em aberto',
      pending: 'Pendente',
      paid: 'Pago',
      overdue: 'Vencido',
      cancelled: 'Cancelado',
      closed: 'Fechado'
    };

    return statusMap[status] || status;
  }

  private static getStatusPalette(status: string): InvoiceStatusPalette {
    const paletteMap: Record<string, InvoiceStatusPalette> = {
      open: {
        fill: this.COLORS.slate100,
        text: this.COLORS.slate700
      },
      pending: {
        fill: [255, 247, 237] as PDFColor,
        text: this.COLORS.amber
      },
      paid: {
        fill: [236, 253, 245] as PDFColor,
        text: this.COLORS.emerald
      },
      overdue: {
        fill: [255, 241, 242] as PDFColor,
        text: this.COLORS.rose
      },
      cancelled: {
        fill: this.COLORS.slate100,
        text: this.COLORS.slate500
      },
      closed: {
        fill: this.COLORS.slate100,
        text: this.COLORS.slate500
      }
    };

    return paletteMap[status] ?? {
      fill: this.COLORS.slate100,
      text: this.COLORS.slate500
    };
  }

  private static formatDate(dateStr: string | undefined): string {
    if (!dateStr) return 'Não informado';
    return formatDateForDisplayFromString(dateStr);
  }

  private static formatMonth(monthStr: string): string {
    try {
      const [year, month] = monthStr.split('-');
      const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1);
      return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    } catch {
      return monthStr;
    }
  }

  private static formatCurrency(amount: number): string {
    return amount.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }

  private static applyTextColor(doc: jsPDF, color: PDFColor): void {
    doc.setTextColor(color[0], color[1], color[2]);
  }

  private static applyFillColor(doc: jsPDF, color: PDFColor): void {
    doc.setFillColor(color[0], color[1], color[2]);
  }

  private static applyDrawColor(doc: jsPDF, color: PDFColor): void {
    doc.setDrawColor(color[0], color[1], color[2]);
  }
}
